import { useState } from "react";
import { WidgetCard, WidgetHeader } from "@/components/dashboard/widgets/shared";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { formatDecimalNb, formatHours, formatNOK } from "@/lib/format";
import { cn } from "@/lib/utils";
import { TONE_TEXT } from "@/lib/widget-status";

export interface HourlyRateRankingProject {
  projectId: string;
  projectName: string;
  projectCode: string | null;
  geekAmount: number;
  loggedHours: number;
  hourlyRate: number | null;
  included: boolean;
  ownershipPct?: number;
}

export interface HourlyRateRankingRow {
  id: string;
  name: string;
  geekAmount: number;
  loggedHours: number;
  hourlyRate: number | null;
  includedProjectCount: number;
  excludedProjectCount: number;
  projects: HourlyRateRankingProject[];
}

interface Props {
  title: string;
  subtitle: string;
  entityLabel: string;
  dialogDescription: string;
  rows: HourlyRateRankingRow[];
  isLoading: boolean;
  isError: boolean;
  errorText: string;
  emptyText: string;
  showOwnership?: boolean;
}

export function HourlyRateRankingPanel({
  title,
  subtitle,
  entityLabel,
  dialogDescription,
  rows,
  isLoading,
  isError,
  errorText,
  emptyText,
  showOwnership = false,
}: Props) {
  const [selected, setSelected] = useState<HourlyRateRankingRow | null>(null);
  const columnCount = showOwnership ? 5 : 4;

  return (
    <>
      <WidgetCard>
        <WidgetHeader
          icon={
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#081F3B" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M4 19 L9 12 L13 15 L20 6" />
              <path d="M20 11 V6 H15" />
            </svg>
          }
          title={title}
          subtitle={subtitle}
        />
        {isLoading ? (
          <div className="h-28 animate-pulse rounded bg-muted" />
        ) : isError ? (
          <p className="text-sm text-red-600">{errorText}</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">{emptyText}</p>
        ) : (
          <div className="overflow-x-auto">
            <div className="min-w-[560px]">
              <div className="flex border-b border-foreground/[0.12] pb-[9px] text-[11px] font-medium uppercase tracking-[0.06em] text-[#8A97A3]">
                <span className="w-10 shrink-0" />
                <span className="flex-1">{entityLabel}</span>
                <span className="w-[110px] shrink-0 text-right">Prosjekter</span>
                <span className="w-[130px] shrink-0 text-right">Timepris</span>
              </div>
              <ol>
                {rows.map((row, index) => (
                  <li key={row.id} className="border-b border-foreground/[0.06] last:border-0">
                    <button
                      type="button"
                      onClick={() => setSelected(row)}
                      className="flex w-full items-center py-[15px] text-left hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      aria-label={`Vis timeprisgrunnlag for ${row.name}`}
                    >
                      <span className="w-10 shrink-0">
                        <span
                          className={cn(
                            "inline-flex h-[26px] w-[26px] items-center justify-center rounded-full font-display text-[13px] font-semibold",
                            index === 0 ? "bg-primary text-white" : "bg-[#EFF1F1] text-[#5A6B7B]",
                          )}
                        >
                          {index + 1}
                        </span>
                      </span>
                      <span className="min-w-0 flex-1 pr-4">
                        <span className="block truncate text-[15.5px] font-semibold text-[#081F3B]">{row.name}</span>
                        <span className="block text-[11.5px] text-[#8A97A3]">
                          {formatNOK(row.geekAmount)} GEEK / {formatHours(row.loggedHours)}
                        </span>
                      </span>
                      <span className="w-[110px] shrink-0 text-right text-[14.5px] text-[#5A6B7B]">
                        {row.includedProjectCount}
                        {row.excludedProjectCount > 0 ? (
                          <span className="block text-[11.5px] text-[#8A97A3]">{row.excludedProjectCount} uten timer</span>
                        ) : null}
                      </span>
                      <span
                        className={cn(
                          "w-[130px] shrink-0 text-right font-display text-[18px] font-semibold tracking-[-0.01em]",
                          index === 0 ? "text-primary" : "text-[#081F3B]",
                        )}
                      >
                        {row.hourlyRate == null ? "—" : formatNOK(Math.round(row.hourlyRate))}
                      </span>
                    </button>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        )}
      </WidgetCard>

      <Dialog open={selected !== null} onOpenChange={(open) => { if (!open) setSelected(null); }}>
        <DialogContent className="max-h-[85vh] max-w-4xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selected?.name ?? entityLabel} — reell timepris</DialogTitle>
            <DialogDescription>{dialogDescription}</DialogDescription>
          </DialogHeader>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="py-2 text-left font-medium">Prosjekt</th>
                  {showOwnership && <th className="py-2 text-right font-medium">Andel</th>}
                  <th className="py-2 text-right font-medium">GEEK reell</th>
                  <th className="py-2 text-right font-medium">Timer</th>
                  <th className="py-2 text-right font-medium">Timepris</th>
                </tr>
              </thead>
              <tbody>
                {(selected?.projects ?? []).map((project) => (
                  <tr key={project.projectId} className="border-b border-border/50 last:border-0">
                    <td className="py-2 pr-4">
                      <span className="font-medium">{project.projectName}</span>
                      {project.projectCode && <span className="ml-2 text-xs text-muted-foreground">{project.projectCode}</span>}
                    </td>
                    {showOwnership && (
                      <td className="py-2 text-right tabular-nums">{formatDecimalNb(project.ownershipPct ?? 0, 1)} %</td>
                    )}
                    <td className="py-2 text-right tabular-nums">{formatNOK(project.geekAmount)}</td>
                    <td className="py-2 text-right tabular-nums">{formatHours(project.loggedHours)}</td>
                    <td className="py-2 text-right tabular-nums font-medium">
                      {project.hourlyRate == null ? (
                        <span className={TONE_TEXT.near} title="Prosjektet er utelatt fra snittet fordi det ikke har førte timer">
                          Utelatt — ingen timer
                        </span>
                      ) : formatNOK(Math.round(project.hourlyRate))}
                    </td>
                  </tr>
                ))}
                {(selected?.projects.length ?? 0) === 0 && (
                  <tr><td colSpan={columnCount} className="py-6 text-center text-muted-foreground">Ingen fullførte prosjekter i perioden.</td></tr>
                )}
              </tbody>
              <tfoot>
                <tr className="border-t font-semibold">
                  <td className="py-3">Sum inkludert</td>
                  {showOwnership && <td />}
                  <td className="py-3 text-right tabular-nums">{formatNOK(selected?.geekAmount ?? 0)}</td>
                  <td className="py-3 text-right tabular-nums">{formatHours(selected?.loggedHours ?? 0)}</td>
                  <td className="py-3 text-right tabular-nums">
                    {selected?.hourlyRate == null ? "—" : formatNOK(Math.round(selected.hourlyRate))}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
